import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

/** Log a 404 hit. Public (no auth). Validates path shape and ignores obvious junk. */
export const logNotFound = createServerFn({ method: "POST" })
  .inputValidator((input) =>
    z.object({
      path: z.string().min(1).max(2048),
      referrer: z.string().max(2048).optional().nullable(),
      userAgent: z.string().max(512).optional().nullable(),
    }).parse(input),
  )
  .handler(async ({ data }) => {
    // Drop noisy paths we don't want cluttering the report.
    const p = data.path;
    if (!p.startsWith("/")) return { ok: false };
    if (/^\/(wp-|api\/|_|favicon|robots|sitemap|manifest|assets\/)/.test(p)) return { ok: false };
    if (/\.(php|asp|aspx|jsp|env|map|ico|png|jpe?g|gif|svg|webp|css|js|woff2?|ttf)$/i.test(p)) return { ok: false };

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    await supabaseAdmin.rpc("log_not_found", {
      _path: p.slice(0, 2048),
      _referrer: data.referrer?.slice(0, 2048) ?? "",
      _user_agent: data.userAgent?.slice(0, 512) ?? "",
    });
    return { ok: true };
  });

/** Admin: list 404s, newest activity first. */
export const getNotFoundReport = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z.object({
      includeResolved: z.boolean().optional(),
    }).optional().parse(input ?? {}),
  )
  .handler(async ({ data, context }) => {
    const { assertAdmin } = await import("./admin.server");
    await assertAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    let q = supabaseAdmin
      .from("not_found_log")
      .select("id, path, hit_count, last_referrer, last_user_agent, first_seen_at, last_seen_at, resolved, note")
      .order("last_seen_at", { ascending: false })
      .limit(500);
    if (!data?.includeResolved) q = q.eq("resolved", false);

    const { data: rows, error } = await q;
    if (error) throw new Error(error.message);
    return { rows: rows ?? [] };
  });

/** Admin: mark a 404 entry resolved (after adding a redirect rule). */
export const resolveNotFound = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z.object({
      id: z.string().uuid(),
      resolved: z.boolean(),
      note: z.string().max(500).optional(),
    }).parse(input),
  )
  .handler(async ({ data, context }) => {
    const { assertAdmin } = await import("./admin.server");
    await assertAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin
      .from("not_found_log")
      .update({
        resolved: data.resolved,
        resolved_at: data.resolved ? new Date().toISOString() : null,
        note: data.note ?? null,
      })
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });
